import React, { useState } from "react";
import { Check, X, Globe, Mail, Phone } from "lucide-react";
import { Chip, EmptyState, Loading, ErrorState, ConfirmButton } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtDate, fmtMoney } from "../lib/format.js";
import { fmtTime, rentalDays } from "../lib/rental-time.js";
import { useBookingRequests, useAcceptRequest, useDismissRequest } from "./api.js";

export default function RequestsTab({ onGoToTab }) {
  const [showHandled, setShowHandled] = useState(false);
  const [err, setErr] = useState("");
  const { notify } = useToast();

  const requests = useBookingRequests();
  const accept = useAcceptRequest();
  const dismiss = useDismissRequest();

  const all = requests.data ?? [];
  const pending = all.filter((r) => !r.processed);
  const handled = all.filter((r) => r.processed);
  const shown = showHandled ? handled : pending;

  return (
    <>
      <div className="notice">
        <b>How this works:</b> requests come from the booking form on
        halcoequipmentrentals.com. Accepting one creates the customer if they are new,
        books the rental as <b>Reserved</b>, and assigns the next invoice number — priced
        from the same rates as any other booking. Nothing is emailed to the customer yet;
        that arrives in Phase 4.
      </div>

      <div className="card">
        <div className="card-h">
          <h2>Website requests</h2>
          <div className="subtabs" style={{ marginBottom: 0, borderBottom: "none" }}>
            <button className={!showHandled ? "on" : ""} onClick={() => setShowHandled(false)}>
              New{pending.length > 0 ? ` (${pending.length})` : ""}
            </button>
            <button className={showHandled ? "on" : ""} onClick={() => setShowHandled(true)}>
              Handled{handled.length > 0 ? ` (${handled.length})` : ""}
            </button>
          </div>
          <div className="spacer" />
          <button className="btn ghost sm" onClick={() => requests.refetch()} disabled={requests.isFetching}>
            {requests.isFetching ? "Checking…" : "Check now"}
          </button>
        </div>

        <div className="card-b">
          {err && <div className="notice red">{err}</div>}

          {requests.isPending ? (
            <Loading what="website requests" />
          ) : requests.error ? (
            <ErrorState error={requests.error} onRetry={() => requests.refetch()} />
          ) : shown.length === 0 ? (
            <EmptyState
              title={showHandled ? "Nothing handled yet" : "No new requests"}
              sub={
                showHandled
                  ? "Accepted and dismissed requests are kept here."
                  : "When someone books through the website, it lands here. The list refreshes every minute."
              }
            />
          ) : (
            shown.map((r) => {
              const days = rentalDays(r);
              return (
                <div
                  key={r.id}
                  style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                    <Chip kind={r.processed ? "returned" : "reserved"}>
                      <Globe size={12} style={{ marginRight: 4 }} />
                      {r.processed ? "Handled" : "New"}
                    </Chip>
                    <b>{r.equipment?.name ?? "No machine specified"}</b>
                    <span className="sub-cell">
                      requested {fmtDate((r.created_at ?? "").slice(0, 10))}
                    </span>
                  </div>

                  <div className="kv" style={{ marginBottom: 10 }}>
                    <dt>Customer</dt>
                    <dd>
                      <b>{r.name}</b>
                      {r.business ? ` · ${r.business}` : ""}
                    </dd>

                    <dt>Contact</dt>
                    <dd style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                      <a href={`mailto:${r.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <Mail size={13} /> {r.email}
                      </a>
                      {r.phone && (
                        <a href={`tel:${r.phone}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <Phone size={13} /> {r.phone}
                        </a>
                      )}
                    </dd>

                    <dt>Dates</dt>
                    <dd>
                      {fmtDate(r.start_date)} {fmtTime(r.start_time)} → {fmtDate(r.end_date)} {fmtTime(r.end_time)}
                      <span className="sub-cell"> · {days} day{days === 1 ? "" : "s"}</span>
                    </dd>

                    {r.equipment && (
                      <>
                        <dt>Rates</dt>
                        <dd className="sub-cell">
                          {fmtMoney(r.equipment.day_rate)}/day · {fmtMoney(r.equipment.week_rate)}/wk ·{" "}
                          {fmtMoney(r.equipment.month_rate)}/mo
                        </dd>
                      </>
                    )}

                    {r.notes && (
                      <>
                        <dt>Notes</dt>
                        <dd style={{ whiteSpace: "pre-wrap" }}>{r.notes}</dd>
                      </>
                    )}
                  </div>

                  {!r.processed && (
                    <div className="duel">
                      <button
                        className="btn primary sm"
                        disabled={accept.isPending || dismiss.isPending}
                        onClick={async () => {
                          setErr("");
                          try {
                            const res = await accept.mutateAsync(r);
                            notify(
                              `Booked as ${res.invoiceNo} — ${res.days} day${res.days === 1 ? "" : "s"}, ` +
                                `${fmtMoney(res.charge)}${res.createdCustomer ? ", new customer created" : ""}. ` +
                                `Open it under Rentals to email the quote.`
                            );
                          } catch (e) {
                            setErr(e.message);
                          }
                        }}
                      >
                        <Check size={15} /> Accept → Reserved
                      </button>
                      <ConfirmButton
                        className="btn ghost sm"
                        disabled={accept.isPending || dismiss.isPending}
                        label={<><X size={15} /> Dismiss</>}
                        confirmLabel="Click again to dismiss"
                        onConfirm={async () => {
                          setErr("");
                          try {
                            await dismiss.mutateAsync(r.id);
                            notify("Request dismissed.");
                          } catch (e) {
                            setErr(e.message);
                          }
                        }}
                      />
                      <div style={{ flex: 1 }} />
                      <button className="btn ghost sm" onClick={() => onGoToTab?.("calendar")}>
                        Check the calendar →
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
